import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { CheckCircle2, AlertTriangle, Loader2, Calendar, Clock, Euro, MapPin, X, ChevronRight } from "lucide-react";

// Public page reached from email/push: shows booking proposal details and
// requires the customer to explicitly confirm, decline, or pick another slot.
export default function ProposalAccept() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [state, setState] = useState("loading"); // loading | review | accepted | declined | already | expired | conflict | error
  const [data, setData] = useState(null); // proposal info
  const [bookedInfo, setBookedInfo] = useState(null); // { booking_id, start_at, ... } after accept
  const [err, setErr] = useState("");

  // Decline panel state
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  // Alternative slots state
  const [showAltSlots, setShowAltSlots] = useState(false);
  const [altLoading, setAltLoading] = useState(false);
  const [altDays, setAltDays] = useState([]); // [{day, slots, reason?}]
  const [altSelectedSlot, setAltSelectedSlot] = useState(null); // ISO

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("error"); setErr("Token mancante"); return; }
    api.get(`/proposals/accept`, { params: { token } })
      .then((r) => {
        if (r.data.already_used) { setState("already"); setData(r.data); return; }
        if (r.data.declined) { setState("declined"); return; }
        setState("review");
        setData(r.data);
      })
      .catch((e) => {
        const status = e.response?.status;
        if (status === 410) setState("expired");
        else if (status === 409) setState("conflict");
        else { setState("error"); setErr(formatErr(e.response?.data?.detail) || "Errore"); }
      });
  }, [token]);

  async function confirmProposal() {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/proposals/accept", { token });
      setBookedInfo(r.data);
      setState("accepted");
    } catch (e) {
      const status = e.response?.status;
      if (status === 410) setState("expired");
      else if (status === 409) {
        setErr("Lo slot proposto non è più disponibile. Scegli un orario alternativo.");
        setShowAltSlots(true);
        loadAltSlots();
      } else {
        setErr(formatErr(e.response?.data?.detail) || "Errore");
      }
    } finally { setBusy(false); }
  }

  async function loadAltSlots() {
    setAltLoading(true);
    try {
      const r = await api.get("/proposals/alternative-slots", { params: { token } });
      setAltDays(r.data.days || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Impossibile caricare gli orari alternativi");
    } finally { setAltLoading(false); }
  }

  async function bookAlternative() {
    if (!altSelectedSlot) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/proposals/book-alternative", { token, start_at: altSelectedSlot });
      setBookedInfo(r.data);
      setState("accepted");
    } catch (e) {
      const status = e.response?.status;
      if (status === 410) setState("expired");
      else if (status === 409) {
        setErr("Quello slot è appena stato preso. Scegline un altro.");
        loadAltSlots();
        setAltSelectedSlot(null);
      } else {
        setErr(formatErr(e.response?.data?.detail) || "Errore");
      }
    } finally { setBusy(false); }
  }

  async function submitDecline() {
    setBusy(true); setErr("");
    try {
      await api.post("/proposals/decline", { token, reason: declineReason });
      setState("declined");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Errore");
    } finally { setBusy(false); }
  }

  const Card = ({ children, className = "" }) => (
    <div className={`max-w-lg mx-auto rounded-3xl bg-white card-soft p-8 mt-12 ${className}`}>{children}</div>
  );

  if (state === "loading") return (
    <Card className="text-center">
      <Loader2 className="h-10 w-10 animate-spin mx-auto text-zinc-400 dark:text-zinc-500" />
      <div className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">Caricamento proposta…</div>
    </Card>
  );

  if (state === "expired") return (
    <Card className="text-center">
      <AlertTriangle className="h-14 w-14 text-amber-500 mx-auto" />
      <h1 className="font-display text-2xl mt-3">Link scaduto</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">Il link è valido per 48 ore. Contatta l'attività per riproporre lo slot.</p>
      <Link to="/" className="btn-secondary inline-block mt-6">Torna alla home</Link>
    </Card>
  );

  if (state === "conflict") return (
    <Card className="text-center">
      <AlertTriangle className="h-14 w-14 text-rose-500 mx-auto" />
      <h1 className="font-display text-2xl mt-3">Slot non più disponibile</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">Qualcuno ha prenotato lo slot prima di te. Contatta l'attività per trovare un altro orario.</p>
      <Link to="/" className="btn-secondary inline-block mt-6">Torna alla home</Link>
    </Card>
  );

  if (state === "already" || state === "accepted") return (
    <Card className="text-center">
      <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
      <h1 className="font-display text-2xl mt-3" data-testid="proposal-accepted">{state === "already" ? "Già confermato" : "Prenotazione confermata!"}</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">
        {(bookedInfo?.business_name || data?.business_name) && <>Da <strong>{bookedInfo?.business_name || data.business_name}</strong>: </>}
        {(bookedInfo?.service_name || data?.service_name) && <strong>{bookedInfo?.service_name || data.service_name}</strong>}
        {(bookedInfo?.start_at || data?.start_at) && <> il {new Date(bookedInfo?.start_at || data.start_at).toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}</>}
      </p>
      <Link to="/me" className="btn-accent inline-block mt-6">Vedi le mie prenotazioni</Link>
    </Card>
  );

  if (state === "declined") return (
    <Card className="text-center">
      <X className="h-14 w-14 text-zinc-400 mx-auto dark:text-zinc-500" />
      <h1 className="font-display text-2xl mt-3" data-testid="proposal-declined-state">Proposta rifiutata</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">Abbiamo informato l'attività. Grazie per la risposta — sei sempre il benvenuto/a per future prenotazioni.</p>
      <Link to="/" className="btn-secondary inline-block mt-6">Torna alla home</Link>
    </Card>
  );

  if (state === "error") return (
    <Card className="text-center">
      <AlertTriangle className="h-14 w-14 text-rose-500 mx-auto" />
      <h1 className="font-display text-2xl mt-3">Errore</h1>
      <p className="text-sm text-zinc-600 mt-2 dark:text-zinc-400">{err}</p>
      <Link to="/" className="btn-secondary inline-block mt-6">Torna alla home</Link>
    </Card>
  );

  // ---- review state ----
  const start = data?.start_at ? new Date(data.start_at) : null;

  return (
    <Card>
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-blue-600 dark:text-blue-400 mb-2">Conferma prenotazione</div>
      <h1 className="font-display text-2xl tracking-tight" data-testid="proposal-review-title">
        {data?.customer_name ? `Ciao ${data.customer_name},` : "Ciao,"}
      </h1>
      <p className="text-sm text-zinc-600 mt-1 dark:text-zinc-400">
        <strong>{data?.business_name}</strong> ti ha proposto questo appuntamento:
      </p>

      {data?.slot_taken && (
        <div className="mt-4 rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-xs text-rose-900 dark:bg-rose-950/40 dark:border-rose-900/50 dark:text-rose-200" data-testid="proposal-slot-taken">
          Lo slot proposto è stato preso da un altro cliente. Puoi però <strong>vedere altri orari</strong> qui sotto.
        </div>
      )}

      <div className="mt-5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 p-4 space-y-2.5" data-testid="proposal-details">
        <div className="flex items-start gap-3">
          <Calendar className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <div className="font-medium capitalize">{start?.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Rome" })}</div>
        </div>
        <div className="flex items-start gap-3">
          <Clock className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">{start?.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">Servizio: {data?.service_name} · {data?.duration_min} min</div>
          </div>
        </div>
        {data?.price > 0 && (
          <div className="flex items-start gap-3">
            <Euro className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">€ {Number(data.price).toFixed(2)}</div>
              {data?.deposit_required && <div className="text-xs text-amber-700 dark:text-amber-400">Caparra richiesta al momento della conferma</div>}
            </div>
          </div>
        )}
        {(data?.business_city || data?.business_address) && (
          <div className="flex items-start gap-3">
            <MapPin className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-zinc-700 dark:text-zinc-300">{[data?.business_address, data?.business_city].filter(Boolean).join(" · ")}</div>
          </div>
        )}
      </div>

      {err && <div className="mt-4 text-sm text-red-600" data-testid="proposal-error">{err}</div>}

      {!showDecline && !showAltSlots && (
        <div className="mt-6 space-y-2">
          {!data?.slot_taken && (
            <button
              type="button"
              onClick={confirmProposal}
              disabled={busy}
              className="btn-accent w-full disabled:opacity-50"
              data-testid="proposal-confirm-btn"
            >
              {busy ? "Conferma in corso…" : "Conferma prenotazione"}
            </button>
          )}
          <button
            type="button"
            onClick={() => { setShowAltSlots(true); loadAltSlots(); }}
            disabled={busy}
            className="btn-secondary w-full"
            data-testid="proposal-alt-slots-btn"
          >
            {data?.slot_taken ? "Vedi altri orari disponibili" : "Non posso, vedi altri orari"}
          </button>
          <button
            type="button"
            onClick={() => setShowDecline(true)}
            disabled={busy}
            className="w-full text-sm text-zinc-500 hover:text-zinc-900 mt-2 dark:text-zinc-400 dark:hover:text-zinc-100"
            data-testid="proposal-decline-open-btn"
          >
            Rifiuta proposta
          </button>
        </div>
      )}

      {/* Decline panel */}
      {showDecline && (
        <div className="mt-6 space-y-3" data-testid="proposal-decline-panel">
          <div className="text-sm font-medium">Vuoi dirci perché? <span className="text-zinc-400 dark:text-zinc-500">(facoltativo)</span></div>
          <textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value.slice(0, 280))}
            rows={3}
            placeholder="Es. Sono in vacanza, preferisco un altro giorno, …"
            className="w-full rounded-2xl bg-zinc-50 border border-transparent focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 px-4 py-3 transition-all outline-none text-sm dark:bg-zinc-800 dark:text-zinc-100 dark:focus:bg-zinc-900"
            data-testid="proposal-decline-reason"
          />
          <div className="text-[11px] text-zinc-400 dark:text-zinc-500 text-right">{declineReason.length}/280</div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setShowDecline(false); setDeclineReason(""); }}
              className="btn-secondary flex-1"
              disabled={busy}
              data-testid="proposal-decline-cancel"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={submitDecline}
              disabled={busy}
              className="btn-primary flex-1 disabled:opacity-50"
              data-testid="proposal-decline-submit"
            >
              {busy ? "Invio…" : "Conferma rifiuto"}
            </button>
          </div>
        </div>
      )}

      {/* Alternative slots panel */}
      {showAltSlots && (
        <div className="mt-6" data-testid="proposal-alt-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="text-sm font-medium">Scegli un altro orario</div>
            <button
              type="button"
              onClick={() => { setShowAltSlots(false); setAltSelectedSlot(null); }}
              className="text-xs text-zinc-500 hover:underline dark:text-zinc-400"
              data-testid="proposal-alt-close"
            >
              Annulla
            </button>
          </div>
          {altLoading ? (
            <div className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-6">Caricamento orari…</div>
          ) : (
            <div className="space-y-4">
              {altDays.map((d) => {
                const dayDate = new Date(d.day + "T12:00:00");
                return (
                  <div key={d.day} data-testid={`alt-day-${d.day}`}>
                    <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-2 dark:text-zinc-400">
                      {dayDate.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", timeZone: "Europe/Rome" })}
                    </div>
                    {(d.slots || []).length === 0 ? (
                      <div className="text-xs text-zinc-400 dark:text-zinc-500">{d.reason || "Nessuno slot disponibile"}</div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {d.slots.map((s) => {
                          const iso = s.start;
                          const active = iso === altSelectedSlot;
                          return (
                            <button
                              key={iso}
                              type="button"
                              onClick={() => setAltSelectedSlot(iso)}
                              className={`h-10 rounded-xl border text-sm font-medium transition ${active ? "bg-blue-600 text-white border-blue-600" : "border-zinc-200 hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:border-zinc-500"}`}
                              data-testid={`alt-slot-${iso}`}
                            >
                              {new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={bookAlternative}
            disabled={!altSelectedSlot || busy}
            className="btn-accent w-full mt-5 disabled:opacity-50"
            data-testid="proposal-alt-confirm-btn"
          >
            {busy ? "Prenotazione…" : altSelectedSlot ? <>Conferma alle {new Date(altSelectedSlot).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })} <ChevronRight className="h-4 w-4 ml-1" /></> : "Seleziona un orario"}
          </button>
        </div>
      )}

      <p className="text-[11px] text-zinc-400 mt-6 text-center dark:text-zinc-500">
        Link valido fino al {data?.expires_at && new Date(data.expires_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
      </p>
    </Card>
  );
}
